import { defineStore } from 'pinia'
import { ref } from 'vue'
import axios from "axios";
import { decodeJwtPayload } from '@/lib/utils';

export const useAuthStore = defineStore('auth', () => {
    const user = ref<Record<string, any> | null>(null)
    const isLoggedIn = () => {
        const accessToken = localStorage.getItem('accessToken');
        if (!accessToken || accessTokenExpired(accessToken)) {
            return false;
        } else {
            user.value = decodeJwtPayload(accessToken);
        }
        return user.value !== null
    }

    function accessTokenExpired(token: string) {
      console.log(token);
        const body = decodeJwtPayload(token);
        const exp = body.exp * 1000;
        return Date.now() >= exp;
    }

    function isAdmin() {
        const accessToken = localStorage.getItem('accessToken');
        if (!accessToken) {
            return false;
        }
        const body = decodeJwtPayload(accessToken);
        return body?.realm_access?.roles.includes('admin');
    }

    function isEmployee() {
        const accessToken = localStorage.getItem('accessToken');
        if (!accessToken) {
            return false;
        }
        const body = decodeJwtPayload(accessToken);
        return body?.realm_access?.roles.includes('employee');
    }

    async function refreshAccessToken(): Promise<boolean> {
        try {
            const response = await axios.post('/api/auth/refresh', {}, { withCredentials: true });
            const { accessToken } = response.data;
            if (accessToken) {
                localStorage.setItem('accessToken', accessToken);
                user.value = decodeJwtPayload(accessToken);
                axios.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`;
                return true;
            }
            return false;
        } catch {
            return false;
        }
    }

    async function ensureUserIsLoggedIn(){
        const skipPaths = ['/info/contact', '/info/support'];
        if (skipPaths.includes(window.location.pathname)) {
            return user;
        }

        // Check if coming back from OAuth with a fresh token in URL
        const accessTokenFromUrl = new URLSearchParams(window.location.search).get('accessToken');
        if (accessTokenFromUrl) {
            localStorage.setItem('accessToken', accessTokenFromUrl);
            user.value = decodeJwtPayload(accessTokenFromUrl);
            axios.defaults.headers.common['Authorization'] = `Bearer ${accessTokenFromUrl}`;
            window.history.replaceState({}, '', window.location.pathname);

            const postReturnRedirect = localStorage.getItem('impersonation.postReturnRedirect');
            if (postReturnRedirect) {
                localStorage.removeItem('impersonation.postReturnRedirect');
                window.location.assign(postReturnRedirect);
            }
            return user;
        }

        // Try to refresh token on every page load to get fresh claims
        const refreshed = await refreshAccessToken();
        if (refreshed) {
            return user;
        }

        // If refresh failed but we have a valid (non-expired) token, use it
        if (isLoggedIn()) {
            const accessToken = localStorage.getItem('accessToken');
            axios.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`;
            return user;
        }

        // No valid session, redirect to login
        window.location.href = `/api/auth/login?redirect=${window.location.pathname}`;
        return user;
    }

    return {
        user,
        isLoggedIn,
        ensureUserIsLoggedIn,
        refreshAccessToken,
        isAdmin,
        isEmployee
    }
})
